import DOMPurify from "dompurify";
import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";
import { resolveApiPath } from "@/lib/utils.ts";
import { resolveCommunicationFileSources } from "@/shared/communicationFilePaths.ts";

// Force every link to open in a new top-level tab instead of navigating the
// sandboxed iframe in place (which would replace the email preview). A
// DOMPurify hook runs at sanitize time and overrides any author-supplied
// target (e.g. `target="_self"`), which a `<base target="_blank">` default
// cannot. rel="noopener noreferrer" drops the opener handle and the Referer
// header, matching the prior behavior.
// Configure DOMPurify to handle Gmail-specific content
const purifyConfig = {
  ADD_TAGS: ["style"],
  ADD_ATTR: ["target", "style", "href", "rel"],
};

const forceLinksToNewTab = (node: Element) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
};

export const CommunicationBody = ({ body, artifactId }: { body: string; artifactId?: string }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (iframeRef.current?.contentWindow) {
      const iframeDocument = iframeRef.current.contentWindow.document;

      let contentToSanitize = body ?? "";

      // Only convert newlines to <br> tags for plain text content (content without HTML tags)
      // HTML emails already have proper <br> tags from the markdown-to-HTML conversion
      // The regex checks for actual HTML tags (not email addresses like <user@example.com>)
      // by requiring the tag name to be followed by whitespace, >, or / (not @ or other chars)
      const hasHTMLTags = /<\/?[a-z][a-z0-9]*(?:[\s>/])/i.test(contentToSanitize);
      if (!hasHTMLTags) {
        // Process plaintext: normalize line endings and group quoted lines
        const lines = contentToSanitize.split(/\r\n|\r|\n/);
        const result: string[] = [];
        let quoteBuffer: string[] = [];

        const flushQuoteBuffer = () => {
          if (quoteBuffer.length > 0) {
            result.push(`<div class="email-quote">${quoteBuffer.join("<br>")}</div>`);
            quoteBuffer = [];
          }
        };

        for (const line of lines) {
          if (/^>+/.test(line)) {
            // Remove the > prefix and add to quote buffer
            const content = line.replace(/^>+\s?/, "");
            quoteBuffer.push(content);
          } else {
            // Flush any pending quotes, then add this line
            flushQuoteBuffer();
            result.push(line);
          }
        }
        flushQuoteBuffer(); // Don't forget trailing quotes

        contentToSanitize = result.join("<br>");
      } else {
        // The server points inline images at the API by an origin-relative
        // path, which this iframe would otherwise load from the app's origin.
        contentToSanitize = resolveCommunicationFileSources(contentToSanitize, resolveApiPath);
      }

      // Create a sanitized version of the email content
      DOMPurify.addHook("afterSanitizeAttributes", forceLinksToNewTab);
      const sanitizedContent = DOMPurify.sanitize(contentToSanitize ?? "", purifyConfig);
      DOMPurify.removeHook("afterSanitizeAttributes");

      // Catch the case where input was non-empty but DOMPurify stripped
      // everything (BIG-4199). Logged here rather than rendered as a separate
      // fallback because the iframe will already render an empty body.
      if (body.trim().length > 0 && sanitizedContent.trim().length === 0) {
        console.error("CommunicationBody: sanitization produced empty output", {
          artifactId,
          inputLength: body.length,
          contentToSanitizeLength: contentToSanitize.length,
        });
        Sentry.captureException(
          new Error("CommunicationBody: sanitization produced empty output"),
          {
            tags: { component: "CommunicationBody" },
            extra: {
              artifactId,
              inputLength: body.length,
              contentToSanitizeLength: contentToSanitize.length,
            },
          },
        );
      }

      // Write the HTML to the iframe
      iframeDocument.open();
      iframeDocument.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              html, body {
                margin: 0;
                padding: 0;
                overflow-y: hidden;
              }
              body {
                font-family: system-ui, -apple-system, sans-serif;
                overflow-wrap: break-word;
                word-wrap: break-word;
                box-sizing: border-box;
                width: 100%;
              }
              * {
                max-width: 100%;
                box-sizing: border-box;
              }
              pre, code {
                white-space: pre-wrap;
                overflow-x: auto;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              table {
                max-width: 100%;
                table-layout: fixed;
                word-break: break-word;
              }
              a {
                cursor: pointer;
                color: #0066cc;
                text-decoration: underline;
              }
              .email-quote {
                color: #666;
                border-left: 3px solid #ccc;
                padding-left: 10px;
                margin: 4px 0;
              }
              @media screen and (max-width: 600px) {
                table, td, tr {
                  width: 100% !important;
                  height: auto !important;
                  display: block !important;
                }
                img {
                  max-width: 100% !important;
                  height: auto !important;
                }
              }
            </style>
          </head>
          <body>${sanitizedContent}</body>
        </html>
      `);
      iframeDocument.close();

      // Adjust iframe height based on content
      const resizeObserver = new ResizeObserver(() => {
        if (iframeRef.current?.contentWindow) {
          const body = iframeRef.current.contentWindow.document.body;
          if (body) {
            iframeRef.current.style.height = `${body.scrollHeight}px`;
          }
        }
      });

      resizeObserver.observe(iframeDocument.body);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [body, artifactId]);

  return (
    <iframe
      ref={iframeRef}
      title="Email Content"
      className="w-full border-0 overflow-hidden"
      style={{ minHeight: "200px", width: "100%" }}
      // No `allow-scripts`: the frame shares the app's origin, so a script in it could lift its own sandbox.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
    />
  );
};
